import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/layout/Header';
import { StatCard } from '../components/common/StatCard';
import { RAGBadge, RAGScoreCircle } from '../components/common/RAGBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  Users,
  UserCog,
  ClipboardList,
  AlertTriangle,
  Clock,
  FileText,
  GraduationCap,
  Pill,
  ArrowRight,
  Calendar,
  TrendingUp,
  Shield,
  Activity,
  CheckCircle2,
  XCircle,
  Bell,
  Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, organization } = useAuth();
  const [stats, setStats] = useState(null);
  const [cqcData, setCqcData] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [readinessData, setReadinessData] = useState(null);
  const [loadingReadiness, setLoadingReadiness] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    fetchReadinessData();
  }, [organization]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, cqcRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, {
          params: { organization_id: organization?.id }
        }),
        axios.get(`${API_URL}/api/cqc/latest`, {
          params: { organization_id: organization?.id }
        }),
        axios.get(`${API_URL}/api/daily-logs`, {
          params: { organization_id: organization?.id }
        })
      ]);

      setStats(statsRes.data);
      setCqcData(cqcRes.data);
      setRecentLogs(logsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReadinessData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/readiness/daily-check`);
      setReadinessData(response.data);
    } catch (error) {
      console.error('Error fetching readiness data:', error);
    } finally {
      setLoadingReadiness(false);
    }
  };

  const getReadinessColor = (status) => {
    switch (status) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-teal-600';
      case 'attention_needed': return 'text-amber-600';
      case 'critical': return 'text-red-600';
      default: return 'text-slate-600';
    }
  };

  const getReadinessBg = (status) => {
    switch (status) {
      case 'excellent': return 'bg-green-50 border-green-200';
      case 'good': return 'bg-teal-50 border-teal-200';
      case 'attention_needed': return 'bg-amber-50 border-amber-200';
      case 'critical': return 'bg-red-50 border-red-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent':
      case 'good':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'attention_needed':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Shield className="w-5 h-5 text-slate-400" />;
    }
  };

  const cqcDomains = [
    { key: 'safe_score', label: 'Safe', icon: Shield },
    { key: 'effective_score', label: 'Effective', icon: Activity },
    { key: 'caring_score', label: 'Caring', icon: Users },
    { key: 'responsive_score', label: 'Responsive', icon: Clock },
    { key: 'well_led_score', label: 'Well-Led', icon: TrendingUp },
  ];

  const quickActions = [
    { label: 'Add Client', path: '/clients/new', icon: Users },
    { label: 'Clock In/Out', path: '/clock', icon: Clock },
    { label: 'New Log Entry', path: '/daily-logs/new', icon: FileText },
    { label: 'Report Incident', path: '/incidents/new', icon: AlertTriangle },
  ];

  if (loading) {
    return (
      <div className="p-8">
        <Header title="Dashboard" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-100 animate-pulse rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <Header title="Dashboard" />
      
      <div className="p-6 space-y-6">
        {/* Welcome banner */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold">
                Welcome back, {user?.first_name}!
              </h2>
              <p className="text-slate-300 mt-1">
                {organization?.name || 'Set up your organization to get started'}
              </p>
            </div>
            <div className="flex gap-3">
              {quickActions.map((action) => (
                <Link key={action.path} to={action.path}>
                  <Button 
                    variant="secondary" 
                    size="sm"
                    className="bg-white/10 hover:bg-white/20 text-white border-0"
                    data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <action.icon className="w-4 h-4 mr-2" />
                    {action.label}
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Clients"
            value={stats?.total_clients || 0}
            icon={Users}
            trend="up"
            trendValue="+12%"
            iconBgColor="bg-blue-100"
            iconColor="text-blue-600"
          />
          <StatCard
            title="Active Staff"
            value={stats?.total_staff || 0}
            icon={UserCog}
            trend="up"
            trendValue="+5%"
            iconBgColor="bg-purple-100"
            iconColor="text-purple-600"
          />
          <StatCard
            title="Open Incidents"
            value={stats?.open_incidents || 0}
            icon={AlertTriangle}
            trend={stats?.open_incidents > 0 ? 'down' : 'neutral'}
            trendValue={stats?.open_incidents > 0 ? '-2' : '0'}
            iconBgColor="bg-amber-100"
            iconColor="text-amber-600"
          />
          <StatCard
            title="Staff Clocked In"
            value={stats?.staff_clocked_in || 0}
            icon={Clock}
            iconBgColor="bg-teal-100"
            iconColor="text-teal-600"
          />
        </div>

        {/* CQC Readiness Widget */}
        <Card className={`border-2 ${readinessData ? getReadinessBg(readinessData.overall_status) : 'border-slate-200'}`} data-testid="readiness-widget">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  readinessData?.overall_status === 'excellent' ? 'bg-green-100' :
                  readinessData?.overall_status === 'good' ? 'bg-teal-100' :
                  readinessData?.overall_status === 'attention_needed' ? 'bg-amber-100' :
                  readinessData?.overall_status === 'critical' ? 'bg-red-100' : 'bg-slate-100'
                }`}>
                  {loadingReadiness ? (
                    <Loader2 className="w-7 h-7 animate-spin text-slate-400" />
                  ) : (
                    <Shield className={`w-7 h-7 ${getReadinessColor(readinessData?.overall_status)}`} />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">CQC Inspection Readiness</h3>
                    {readinessData && getStatusIcon(readinessData.overall_status)}
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">
                    {loadingReadiness ? 'Checking compliance...' : 
                     readinessData?.quick_summary || 'Set up your organization to see readiness score'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                {readinessData && (
                  <>
                    {/* Score Circle */}
                    <div className="text-center">
                      <div className={`text-4xl font-bold ${getReadinessColor(readinessData.overall_status)}`}>
                        {readinessData.overall_score}%
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Today&apos;s Score</p>
                    </div>

                    {/* Area Status Pills */}
                    <div className="hidden md:flex flex-wrap gap-2 max-w-xs">
                      {readinessData.areas_checked && Object.entries(readinessData.areas_checked).slice(0, 4).map(([area, status]) => (
                        <Badge 
                          key={area}
                          variant="outline"
                          className={`text-xs ${
                            status === 'excellent' ? 'bg-green-100 text-green-700 border-green-200' :
                            status === 'good' ? 'bg-teal-100 text-teal-700 border-teal-200' :
                            status === 'attention_needed' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            'bg-red-100 text-red-700 border-red-200'
                          }`}
                        >
                          {area.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>

                    {/* Urgent Actions Count */}
                    {readinessData.urgent_actions?.length > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 rounded-lg">
                        <Bell className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-700">
                          {readinessData.urgent_actions.length} action{readinessData.urgent_actions.length > 1 ? 's' : ''} needed
                        </span>
                      </div>
                    )}
                  </>
                )}

                <Link to="/cqc-readiness">
                  <Button variant="outline" size="sm" className="whitespace-nowrap">
                    View Details
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* CQC Compliance Card */}
          <Card className="lg:col-span-2" data-testid="cqc-compliance-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                CQC Compliance Overview
              </CardTitle>
              <Link to="/compliance">
                <Button variant="ghost" size="sm">
                  View Details
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-8">
                <div className="flex flex-col items-center">
                  <RAGScoreCircle 
                    score={cqcData?.overall_score || 0} 
                    rating={cqcData?.overall_rating || 'good'}
                    size="lg"
                  />
                  <p className="mt-2 text-sm text-slate-500">Overall Rating</p>
                  <RAGBadge rating={cqcData?.overall_rating || 'good'} size="sm" className="mt-1" />
                </div>
                
                <div className="flex-1 space-y-4">
                  {cqcDomains.map((domain) => (
                    <div key={domain.key} className="flex items-center gap-4">
                      <domain.icon className="w-4 h-4 text-slate-400" />
                      <span className="w-24 text-sm text-slate-600">{domain.label}</span>
                      <div className="flex-1">
                        <Progress 
                          value={(cqcData?.[domain.key] || 0) / 4 * 100} 
                          className="h-2"
                        />
                      </div>
                      <span className="w-8 text-sm font-medium text-slate-900">
                        {cqcData?.[domain.key]?.toFixed(1) || '0.0'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card data-testid="quick-stats-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-teal-600" />
                Today's Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Active Care Plans</p>
                    <p className="text-xs text-slate-500">Updated today</p>
                  </div>
                </div>
                <span className="text-xl font-bold text-slate-900">{stats?.active_care_plans || 0}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Pill className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Medications Due</p>
                    <p className="text-xs text-slate-500">Today</p>
                  </div>
                </div>
                <span className="text-xl font-bold text-slate-900">{stats?.medications_due_today || 0}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Training Compliance</p>
                    <p className="text-xs text-slate-500">Staff completion rate</p>
                  </div>
                </div>
                <span className="text-xl font-bold text-slate-900">{stats?.training_compliance?.toFixed(0) || 0}%</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Pending Documents</p>
                    <p className="text-xs text-slate-500">Awaiting signature</p>
                  </div>
                </div>
                <span className="text-xl font-bold text-slate-900">{stats?.pending_documents || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card data-testid="recent-activity-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-teal-600" />
              Recent Daily Logs
            </CardTitle>
            <Link to="/daily-logs">
              <Button variant="ghost" size="sm">
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentLogs.length > 0 ? (
              <div className="space-y-4">
                {recentLogs.map((log, index) => (
                  <div 
                    key={log.id || index}
                    className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg"
                  >
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {log.log_type || 'General'} Log
                      </p>
                      <p className="text-sm text-slate-500 line-clamp-2">
                        {log.content}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>No recent logs</p>
                <Link to="/daily-logs/new">
                  <Button variant="outline" size="sm" className="mt-3">
                    Create First Log
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
